import React from 'react';
import * as Progress from '@radix-ui/react-progress';
import styled, { css } from 'styled-components';
import colors from '../../style/colors';
import { sizes, variants } from './style';

interface ProgressIndicationProps {
  transition?: boolean;
  transitionDuration?: number;
  progressVariant?: keyof typeof variants;
}

export interface ProgressProps extends ProgressIndicationProps {
  'data-testid'?: string;
  size?: keyof typeof sizes;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  value?: number;
  getValueLabel?: (value: number, max: number) => string;
  style?: React.CSSProperties;
}

const StyledProgressRoot = styled(Progress.Root)<ProgressProps>`
  height: ${props => props.size && sizes[props.size].height}px;
  border: 1px solid ${colors.gray300};
  border-radius: ${props => props.size && sizes[props.size].borderRadius}px;
  background-color: ${colors.gray100};
  overflow: hidden;
`;

const StyledIndicator = styled(Progress.Indicator).withConfig({
  shouldForwardProp: prop => !['transitionDuration', 'progressVariant', 'transition'].includes(prop),
})<ProgressIndicationProps>`
  background-color: ${props =>
    props.progressVariant
      ? variants[props.progressVariant]?.({ theme: props.theme })?.background
      : props.theme.actionColor};
  height: 100%;
  border-radius: 20px;
  ${props =>
    props.transition &&
    css`
      transition: transform 2000ms cubic-bezier(0.65, 0, 0.35, 1);
    `};
  transition-duration: ${props => props.transitionDuration && `${props.transitionDuration}ms`};
`;

const ProgressBar = ({
  size = 'sm',
  transition = true,
  progressVariant,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  value,
  getValueLabel = () => ariaLabel || ariaLabelledby || '',
  'data-testid': dataTestId,
  style,
}: ProgressProps) => {
  return (
    <StyledProgressRoot
      data-testid={dataTestId}
      size={size}
      className={'box-progress-bar'}
      value={value}
      getValueLabel={getValueLabel}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledby}
      style={style}
    >
      <StyledIndicator
        style={{
          transform: `translateX(-${100 - (value || 0)}%)`,
        }}
        progressVariant={progressVariant}
        transition={transition}
        transitionDuration={1000}
      />
    </StyledProgressRoot>
  );
};

export default ProgressBar;
